/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import React, { ReactNode } from "react";
import { Icon } from "../Icon";
import SyncIcon from "@mui/icons-material/Sync";
import "./Status.css";
import WarningIcon from "@mui/icons-material/Warning"
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

const StatusIcon: React.FC<{ status: string; isLabel?: boolean; tooltip?: ReactNode | string }> = ({ status, isLabel = true, tooltip = "" }) => {
  const statusIcons: Record<string, React.ReactNode> = {
    Disconnected: <Icon name="link_off" size={16} className="text-destructive" />,
    Connected: <Icon name="link" size={16} className="text-success" />,
    Successful: <Icon name="check_circle" size={16} className="text-emerald-600 dark:text-emerald-500" />,
    Normal: <Icon name="check_circle" size={16} className="text-success" />,
    Critical: <Icon name="error" size={18} className="text-destructive" />,
    Complete: <Icon name="check_circle" size={16} className="text-success" />,
    Major: <Icon name="error_outline" size={18} className="text-warning" />,
    Minor: <Icon name="warning" size={18} className="text-yellow-500" />,
    Unknown: <Icon name="help" size={16} className="text-muted-foreground" />,
    Info: <Icon name="info" size={18} className="text-muted-foreground" />,
    Cleared: <Icon name="check_circle" size={16} className="text-success" />,
    clear: <CheckCircleIcon className="status-icon" color="success" />,
    Warning: <Icon name="warning_amber" size={16} className="text-yellow-500" />,
    Up: <Icon name="check_circle" size={16} className="text-success" />,
    Down: <Icon name="warning" size={16} className="text-warning" />,
    "IS-NORMAL": <Icon name="check_circle" size={16} className="text-success" />,
    "IS-IDLE": <Icon name="check_circle" size={16} className="text-success" />,
    "IS-NETMON": <Icon name="check_circle" size={16} className="text-success" />,
    "OOS-DISABLED": <Icon name="error" size={16} className="text-destructive" />,
    "OOS-FAULT": <Icon name="error" size={16} className="text-destructive" />,
    "IS-DEGRADED": <Icon name="error" size={16} className="text-destructive" />,
    "OOS-INHERITED": <Icon name="error" size={16} className="text-destructive" />,
    "OOS-NOTPRESENT": <Icon name="error" size={16} className="text-destructive" />,
    SERVER_DOWN: <Icon name="error" size={16} className="text-destructive" />,
    SUCCESSFUL: <Icon name="check_circle" size={16} className="text-emerald-600 dark:text-emerald-500" />,
    FAILED: <Icon name="error" size={16} className="text-destructive" />,
    Failed: <Icon name="error" size={16} className="text-destructive" />,
    COMMITTED: <Icon name="sync" size={16} className="text-foreground" />,
    SENT: <Icon name="sync" size={16} className="text-foreground" />,
    INIT: <Icon name="sync" size={16} className="text-foreground" />,
    RECEIVED: <Icon name="sync" size={16} className="text-foreground" />,
    SUCCESS: <Icon name="check_circle" size={16} className="text-success" />,
    NoLink: <Icon name="sync_disabled" size={16} className="text-destructive" />,
    InProgress: <SyncIcon sx={{ fontSize: 16, color: "var(--foreground)" }} />,
    INPROGRESS: <SyncIcon sx={{ fontSize: 16, color: "var(--foreground)" }} />,
    InMaintenance: <WarningIcon className="status-icon" color="warning" />,
    Running: <SyncIcon sx={{ fontSize: 16, color: "var(--foreground)" }} />,
    RUNNING: <SyncIcon sx={{ fontSize: 16, color: "var(--foreground)" }} />,
    Idle: <SyncIcon sx={{ fontSize: 16, color: "var(--foreground)" }} />,
    IDLE: <SyncIcon sx={{ fontSize: 16, color: "var(--foreground)" }} />,
    FAIL: <Icon name="sync_problem" size={16} className="text-destructive" />,
    Error: <Icon name="priority_high" size={16} className="text-destructive" />,
    Pending: <Icon name="hourglass_empty" size={16} className="text-success" />,
    Initiated: <Icon name="outbound" size={16} className="text-success" style={{ transform: "rotate(45deg)" }} />,
    Indeterminate: <Icon name="remove_circle_outline" size={16} className="text-muted-foreground" />,
    Optimal: <Icon name="check_circle_outline" size={16} className="text-success" />,
    Suboptimal: <Icon name="warning" size={16} className="text-warning" />,
    Degraded: <Icon name="error_outline" size={16} className="text-destructive" />,
    Syncing: (
      <SyncIcon
        sx={{
          fontSize: 16,
          color: "var(--foreground)",
          animation: "spin 2s linear infinite",
          "@keyframes spin": {
            "0%": { transform: "rotate(360deg)" },
            "100%": { transform: "rotate(0deg)" },
          },
        }}
      />
    ),
    Done: <Icon name="check" size={16} className="text-success" />,
    Enabled: <Icon name="check_circle" size={16} style={{ color: "#4ADE80" }} />,
    Disabled: <Icon name="cancel" size={16} style={{ color: "#94A3B8" }} />,
  };

  const icon = statusIcons[status] || statusIcons.Unknown;

  if (isLabel) {
    return (
      <span title={`${tooltip}`} className="inline-flex items-center gap-2" style={{ fontSize: "var(--font-size-sm)" }}>
        <span className="shrink-0">{icon}</span>
        <span style={{ color: "var(--foreground)" }}>{status || "Unknown"}</span>
      </span>
    );
  }

  return <>{icon}</>;
};

export default StatusIcon;
